<template>
  <v-card v-if="model">
    <v-card-title class="headline">{{
      editAction ? "Update action" : "Add new action"
    }}</v-card-title>
    <v-card-text>
      <RfTextInput
        v-model="model.args.description"
        variant="outlined"
        density="compact"
        hideDetails
        :disabled="!!disabled"
        placeholder="Your API Action description"
        title="Action Description"
      />
      <div class="my-5 flex flex-col">
        <span>
          To include dynamic values, embed a placeholder in the url, query string, headers, or
          payload.
        </span>
        <a @click.stop="availableDynamicValuesListDialog = true"
          >Available Dynamic Value Placeholders</a
        >
      </div>
      <v-dialog v-model="availableDynamicValuesListDialog" scrollable maxWidth="700px">
        <v-card>
          <v-card-title>
            <span>Available Dynamic Value Placeholders</span>
          </v-card-title>
          <v-card-text>
            <ul class="availableDynamicValuesList">
              <li>%user_id%</li>
              <li>%promo_inputs.promo_input_1_value%</li>
              <li v-for="value in availableDynamicValues" :key="value">{{ value }}</li>
            </ul>
          </v-card-text>
          <v-card-actions class="modal-card-actions-div">
            <v-btn
              class="cancel-btn px-7!"
              variant="outlined"
              @click="availableDynamicValuesListDialog = false"
              >Close</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-dialog>
      <div v-for="(input, index) in model.args.global_inputs" :key="index">
        <RfTextInput
          v-if="input.input_type === 'text_input'"
          v-model="input.value"
          variant="outlined"
          density="compact"
          :disabled="!!disabled"
          :title="input.value_label"
          placeholder=" "
        />
        <RfSelect
          v-if="input.input_type === 'select'"
          v-model="input.value"
          variant="outlined"
          density="compact"
          :disabled="!!disabled"
          :title="input.value_label"
          :items="input.possible_values"
        />
        <div v-if="input.input_type === 'nested' && canShowInput(input)">
          <v-dialog v-if="addKeyValueModal" v-model="addKeyValueModal" maxWidth="550px">
            <v-card>
              <v-card-title>
                <span>Add a key/value pair to {{ addKeyValueModal.value_description }}</span>
              </v-card-title>
              <v-card-text>
                <RfTextInput
                  v-model="newNested.key"
                  placeholder=" "
                  variant="outlined"
                  density="compact"
                  title="Key"
                />
                <RfTextInput
                  v-model="newNested.value"
                  placeholder=" "
                  variant="outlined"
                  density="compact"
                  title="Value"
                  hideDetails
                />
              </v-card-text>
              <v-card-actions class="modal-card-actions-div">
                <v-btn
                  width="100"
                  class="cancel-btn"
                  variant="outlined"
                  @click.stop="addKeyValueModal = null"
                  >Cancel</v-btn
                >
                <v-btn width="150" variant="flat" color="primary" @click="addPair(addKeyValueModal)"
                  >Submit</v-btn
                >
              </v-card-actions>
            </v-card>
          </v-dialog>
          <div class="customKeysValuesDiv">
            <div class="customKeysValuesDivHeader">{{ input.value_label }}</div>
            <v-table class="rf-settings-table-data" style>
              <template #default>
                <thead>
                  <tr>
                    <th class="text-left">Key</th>
                    <th>Value</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(value, key) in input.value" :key="key">
                    <td>{{ key }}</td>
                    <td>{{ value }}</td>
                    <td align="right">
                      <RfButton
                        icon="md:clear"
                        size="small"
                        variant="text"
                        color="error"
                        :disabled="disabled"
                        @click="deletePair(input, key)"
                      />
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-table>
            <div class="flex justify-end">
              <RfButton
                :buttonText="`Add a key/value pair to ${input.value_description}`"
                size="small"
                color="success"
                :disabled="disabled"
                @click.stop="addKeyValueModal = input"
              />
            </div>
          </div>
        </div>
      </div>
    </v-card-text>
    <v-card-actions class="modal-card-actions-div">
      <v-btn width="100" class="cancel-btn" variant="outlined" @click="cancel">
        {{ !disabled ? "Cancel" : "Close" }}</v-btn
      >
      <RfButton
        :buttonText="submitCTA"
        class="px-7!"
        color="primary"
        :disabled="disabled || !submitEnabled"
        @click="submitAction"
      />
    </v-card-actions>
  </v-card>
</template>

<script>
import RfSelect from "@/components/legacy/RfSelectLegacy.vue";
import RfTextInput from "@/components/legacy/RfInputLegacy.vue";
import RfButton from "@/components/buttons/RfButton.vue";
import { cloneDeep } from "lodash-es";
import { useLegacyTmpStore } from "@/pinia/legacyTmpStore";
import { useCustomFields } from "@/utils/composables/useCustomFields";

export default {
  name: "RfCustomConnectorActions",
  components: { RfTextInput, RfSelect, RfButton },
  props: ["app", "editAction", "isNew", "providerInputs", "disabled"],
  setup: props => ({
    legacyTmpStore: useLegacyTmpStore(),
    customFields: useCustomFields({ app: props.app }),
  }),
  data() {
    return {
      newNested: { key: "", value: "" },
      editingNested: "",
      model: cloneDeep(this.editAction),
      requiredInputs: ["base_url", "method"],
      requiredArgs: ["description"],
      availableDynamicValuesListDialog: false,
      addKeyValueModal: null,
    };
  },
  computed: {
    newAction() {
      return this.legacyTmpStore.customConnectorAction;
    },
    submitCTA() {
      return this.isNew ? "Create New Action" : "Update Action";
    },
    submitEnabled() {
      if (!this.model) return false;
      const blankArg = this.requiredArgs.find(arg => !this.model.args[arg]);
      if (blankArg) {
        return false;
      }
      const globals = this.model.args.global_inputs;
      const blankInput = this.requiredInputs.find(input => {
        const globalInput = globals.find(global => global.value_description === input);
        return !globalInput.value;
      });
      if (blankInput) {
        return false;
      }
      return true;
    },
    availableUserTraits() {
      return this.customFields.appCustomFields.value.map(field => `%user.${field.label}%`);
    },
    availableProviderTraits() {
      if (this.providerInputs) {
        return this.providerInputs.map(input => `%provider.${input}%`);
      }
      return [];
    },
    availableDynamicValues() {
      return this.availableUserTraits.concat(this.availableProviderTraits);
    },
    isGetRequest() {
      if (this.model && this.model.args) {
        const methodInput = this.model.args.global_inputs.find(
          input => input.value_description === "method",
        );
        if (methodInput) {
          return methodInput.value === "GET";
        }
      }
      return false;
    },
  },
  watch: {
    newAction(to) {
      if (to && !this.model) {
        this.model = cloneDeep(this.newAction);
      }
    },
    editAction(to) {
      if (to) {
        this.model = cloneDeep(to);
      } else {
        this.model = cloneDeep(this.newAction);
      }
    },
  },
  mounted() {
    this.legacyTmpStore.newConnectorAction(this.app.id);
  },
  methods: {
    cancel() {
      this.$emit("close");
    },
    submitAction() {
      // clear payload if get
      if (this.isGetRequest) {
        const payloadInput = this.model.args.global_inputs.find(
          input => input.value_description === "payload",
        );
        if (payloadInput) {
          payloadInput.value = {};
        }
      }
      if (this.isNew) {
        this.$emit("addCustomConnectorAction", cloneDeep(this.model));
      } else {
        this.$emit("updateAction", this.model);
      }
    },
    addPair(input) {
      const { key } = this.newNested;
      const { value } = this.newNested;
      if (input && key && value) {
        input.value[key] = value;
        this.newNested.key = "";
        this.newNested.value = "";
      }
      this.editingNested = "";
      this.addKeyValueModal = null;
    },
    openNewPairForm(input) {
      this.newNested.key = "";
      this.newNested.value = "";
      this.editingNested = input.value_description;
    },
    deletePair(input, key) {
      delete input.value[key];
      this.$forceUpdate();
    },
    canShowInput(input) {
      if (this.isGetRequest) {
        return input.value_description !== "payload";
      }
      return true;
    },
  },
};
</script>
